import { useQuery } from "@tanstack/react-query";
import { ListChecks, Plus } from "lucide-react";
import { useTranslation } from "react-i18next";
import { Navigate, useNavigate } from "react-router-dom";

import { getPosClient } from "../lib/api";
import { usePageTitle } from "../lib/use-page-title";
import { POS_ORDER_CREATE, POS_PAYMENT_INITIATE } from "../lib/permissions";
import { Button } from "../components/ui/Button";
import { Card } from "../components/ui/Card";
import { PageSpinner } from "../components/ui/Spinner";

/**
 * "/" landing - role-aware.
 *
 *   * Cashiers + supervisors (anyone with pos.payment.initiate) go
 *     straight to /sale - that's their job, no daily-summary needed.
 *
 *   * Managers + reporters get a small dashboard placeholder with
 *     CTAs to /sale and /transactions until POS-1.4 ships real
 *     metrics.
 *
 *   * Anyone else (no relevant POS perms) sees a friendly notice.
 */
export function HomePage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  usePageTitle("home");
  const profile = useQuery({
    queryKey: ["profile"],
    queryFn: () => getPosClient().getProfile(),
  });

  if (profile.isPending) return <PageSpinner label={t("common.loading")} />;
  if (!profile.data) return <Navigate to="/login" replace />;

  const perms = new Set(profile.data.permissions);
  if (perms.has(POS_ORDER_CREATE) && perms.has(POS_PAYMENT_INITIATE)) {
    return <Navigate to="/sale" replace />;
  }

  // Read-only / reporter dashboard.
  const greeting = profile.data.first_name
    ? t("home.greeting", { name: profile.data.first_name })
    : t("home.welcome");
  const active = profile.data.active_membership;

  return (
    <div className="max-w-3xl space-y-6">
      <header>
        <h1 className="text-2xl font-semibold text-strong">{greeting}</h1>
        {active && (
          <p className="mt-1 text-sm text-muted">
            {t("home.signedInAs", {
              location:
                active.location?.name ?? active.organization?.name ?? active.partner?.name ?? "-",
              role: active.role_slug.replace(/_/g, " "),
            })}
          </p>
        )}
      </header>

      <Card title={t("home.salesTitle")}>
        <p className="text-sm text-muted">{t("home.salesBody")}</p>
        <div className="mt-4 flex flex-wrap gap-3">
          <Button onClick={() => navigate("/sale")} leftIcon={<Plus className="h-4 w-4" />}>
            {t("home.salesCta")}
          </Button>
          <Button
            variant="secondary"
            onClick={() => navigate("/transactions")}
            leftIcon={<ListChecks className="h-4 w-4" />}
          >
            {t("home.transactionsCta")}
          </Button>
        </div>
      </Card>

      <Card title={t("home.permsTitle")}>
        <p className="text-xs text-muted">{t("home.permsHint")}</p>
        <ul className="mt-3 flex flex-wrap gap-1.5">
          {profile.data.permissions.length === 0 ? (
            <li className="text-xs text-muted">{t("home.permsEmpty")}</li>
          ) : (
            profile.data.permissions.map((code) => (
              <li
                key={code}
                className="rounded bg-surface-strong px-2 py-1 font-mono text-[0.6875rem] text-foreground"
              >
                {code}
              </li>
            ))
          )}
        </ul>
      </Card>
    </div>
  );
}
